<template>
    <div class="mb-4 md:mb-6">
        <div class="flex flex-col gap-4 w-full">
            <div v-if="computedHomeHref || computedBackButtonHref" class="flex flex-row items-center gap-2">
                <vx-button
                    v-if="computedHomeHref"
                    color="muted-dark"
                    size="sm"
                    :href="computedHomeHref"
                    data-test="home-btn"
                    aria-label="Franchise Hub"
                >
                    <font-awesome-icon widthAuto :icon="faHome" />
                </vx-button>
                <vx-button
                    v-if="computedBackButtonHref"
                    color="muted-dark"
                    size="sm"
                    :href="computedBackButtonHref"
                    data-test="back-btn"
                >
                    {{ backButtonText }}
                    <font-awesome-icon widthAuto :icon="faSquareArrowUpLeft" />
                </vx-button>
            </div>

            <div class="flex flex-row items-center flex-wrap gap-4">
                <template v-for="(item, index) in breadcrumbItems" :key="index">
                    <font-awesome-icon
                        widthAuto
                        v-if="item.icon"
                        class="text-3xl font-extrabold text-slate-500"
                        :icon="item.icon"
                    />
                    <span v-if="index < breadcrumbItems.length - 1" class="text-3xl font-extrabold text-slate-500">
                        {{ item.label }}
                    </span>
                    <h1 v-else class="m-0 text-3xl font-extrabold text-slate-700">
                        {{ item.label }}
                    </h1>
                    <span v-if="index < breadcrumbItems.length - 1" class="text-slate-800 text-base"> / </span>
                </template>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { faHome, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxButton } from '@voxie/frontend-components';
import { stringifyQueryString } from '~/utils/string';
import { analyticsCategoriesValues, getCategoryByValue } from '~/constants/categories';
import { getMetricByValue, metricsWithGoals } from '~/utils/analytics';

const props = defineProps({
    homeHref: {
        type: String,
        default: '/analytics/franchise-hub',
    },
    backButtonHref: {
        type: String,
        required: false,
    },
    backButtonText: {
        type: String,
        default: 'Back',
    },
    pageTitle: {
        type: String,
        required: false,
    },
    fetchParams: {
        type: Object,
        required: false,
    },
    category: {
        type: String,
        required: false,
        validator: (category) => analyticsCategoriesValues.includes(category),
    },
    metric: {
        type: String,
        required: false,
        validator: (metric) => metricsWithGoals.includes(metric),
    },
});

const activeCategory = computed(() => getCategoryByValue(props.category));
const activeMetric = computed(() => getMetricByValue(props.metric));

const computedHomeHref = computed(() => {
    if (!props.homeHref) {
        return null;
    }

    if (!props.fetchParams) {
        return props.homeHref;
    }

    return `${props.homeHref}?${stringifyQueryString(props.fetchParams)}`;
});

const computedBackButtonHref = computed(() => {
    if (!props.backButtonHref) {
        return null;
    }

    if (!props.fetchParams) {
        return props.backButtonHref;
    }

    return `${props.backButtonHref}?${stringifyQueryString(props.fetchParams)}`;
});

const breadcrumbItems = computed(() => {
    const items = [];

    if (activeCategory.value) {
        items.push({
            icon: activeCategory.value?.icon,
            label: activeCategory.value?.label,
        });

        if (activeMetric.value) {
            items.push({ label: activeMetric.value?.label });
        }
    }

    if (props.pageTitle) {
        items.push({ label: props.pageTitle });
    }

    return items;
});
</script>
